import type { Transcript } from '../../../Transcript';
import type { TextMetrics } from '../metrics/TextMetrics';
import { DynamicLevel } from '../../../../enums';
import { TextDynamicAnimationBase } from './TextDynamicAnimationBase';

/**
 * Dynamic animation specific for text style.
 * When animating the text (instead of graphics around the text), we need to spread the values to all the words
 * in the text, since the text renderer handles each word separately.
 */
export class TextStyleDynamicAnimationRenderer<
    PropertyType extends string = string,
> extends TextDynamicAnimationBase<PropertyType> {
    protected override _computeWordTranscriptAnimationValues(
        currentTime: number,
        transcript: Transcript,
        textMetrics: TextMetrics,
    ) {
        super._computeWordTranscriptAnimationValues(currentTime, transcript, textMetrics);
        this.computedValues = textMetrics.spreadLevelValues(this.computedValues, DynamicLevel.word);
    }

    protected override _computeLineTranscriptAnimationValues(
        currentTime: number,
        transcript: Transcript,
        textMetrics: TextMetrics,
    ) {
        super._computeLineTranscriptAnimationValues(currentTime, transcript, textMetrics);
        this.computedValues = textMetrics.spreadLevelValues(this.computedValues, DynamicLevel.line);
    }

    /**
     * Used to spread the values driven by the animation manager to all the words.
     * An important part of this is to repeat the same value for words that should be grouped within a level.
     * For example, spreading the same value to all words in a line for line level animation.
     */
    spreadAnimatedTextValuesToLevels(textMetrics: TextMetrics) {
        this.computedValues = textMetrics.spreadLevelValues(this.valuesToAnimate, this.level);
    }

    /**
     * Get the number of objects drawn for the given level when animating text.
     * This is important to know for how many values there should be to animate.
     */
    getNumberOfLevelObjectsForText(textMetrics: TextMetrics): number {
        switch (this.level) {
            case DynamicLevel.object:
                return 1;
            case DynamicLevel.line:
                return textMetrics.lines.length;
            case DynamicLevel.word:
                return textMetrics.wordMetricsWithoutSpaces.length;
            case DynamicLevel.highlight:
                return textMetrics.highlightSpans.length;
            default:
                throw new Error(`Unsupported dynamic level ${this.level}`);
        }
    }
}
